import classnames from 'classnames';
import React from 'react';
import { Link } from 'react-router-dom';

import { ButtonIcon } from 'src/app/components/Button/ButtonIcon';
import { IconContent } from 'src/app/components/Icon/IconContent';
import { IconTrash } from 'src/app/components/Icon/IconTrash';
import { IconViews } from 'src/app/components/Icon/IconViews';
import { VideoItemProgramName } from 'src/app/components/Video/VideoItemProgramName';
import { VideoThumbnail } from 'src/app/components/Video/VideoThumbnail';
import { GA_EVENT_CATEGORY } from 'src/app/constants/analytics';
import { PARAMS, VIDEO_WATCH } from 'src/app/constants/route';
import { VIDEO_ITEM_TYPE } from 'src/app/constants/video';
import { EventTrack, pushEventTracking } from 'src/app/helpers/analytics';
import { formatViewCountNumber } from 'src/app/helpers/format';
import { withStyles } from 'src/app/helpers/styles';
import { UserPlaylists } from 'src/app/models/user';
import { VideoCard } from 'src/app/models/video';
import { AddToWatchlistFunction } from 'src/app/models/watchlist';

interface MyProps {
  type: VIDEO_ITEM_TYPE;
  video: {
    description?: string;
  } & VideoCard;
  className?: string;
  eventTrack?: EventTrack;
  isShowViewCount?: boolean;
  onTrashButtonClick?: VoidFunction;
  onWatchlistButtonClick?: AddToWatchlistFunction;
  queryParams?: string;
  railAnalyticsVideo?: string;
  railAnalyticsProgram?: string;
  user?: UserPlaylists;
  eventLabel?: string;
}

@withStyles({
  base: [require('./VideoItem.base.scss')],
  desktop: [require('./VideoItem.desktop.scss')]
})
export class VideoItem extends React.Component<MyProps> {
  constructor(props: MyProps) {
    super(props);

    this.pushAnalytics = this.pushAnalytics.bind(this);
  }

  shouldComponentUpdate(nextProps: MyProps): boolean {
    return this.props.video.id !== nextProps.video.id ||
      this.props.video.isInWatchlist !== nextProps.video.isInWatchlist ||
      this.props.video.viewCount !== nextProps.video.viewCount ||
      this.props.video.inPlaylists.length !== nextProps.video.inPlaylists.length ||
      this.props.className !== nextProps.className ||
      this.props.user?.playlists?.length !== nextProps.user?.playlists?.length;
  }

  render(): JSX.Element {
    const { className, onWatchlistButtonClick, queryParams, railAnalyticsVideo, railAnalyticsProgram, video, type, user, eventLabel } = this.props;
    const videoPath = VIDEO_WATCH.replace(PARAMS.ID, video.id);
    const eventTrack = eventLabel ? {
      'eventDetails.category': GA_EVENT_CATEGORY.ADD_BUTTON,
      'eventDetails.action': 'intent to add',
      'eventDetails.label': eventLabel,
      custom: {
        video_id: video.id,
        title: video.title,
        program_id: video.program.id,
        program: video.program.name
      }
    } : undefined;
    return (
      <div className={classnames('d-flex w-100 video-item', 'video-item--' + type, className)}>
        <VideoThumbnail
          {...video}
          className={classnames('flex-noshrink', 'video-item__thumbnail--' + type)}
          isLarge={type === VIDEO_ITEM_TYPE.COLUMN_LARGE}
          onWatchlistButtonClick={onWatchlistButtonClick ? onWatchlistButtonClick(video, eventTrack) : undefined}
          pushAnalytics={this.pushAnalytics}
          queryParams={queryParams}
          railAnalytics={railAnalyticsVideo}
          videoPath={videoPath}
          showAddToMenu={!!user}
          userPlaylists={user?.playlists}
          addToPushEvent={eventTrack}
        />

        <div className={classnames('position-relative mt@md-8 pl-4 pl@md-0', 'video-item__text-container--' + type)}>
          <div className={classnames('fw-500 mb-8', 'video-item__title--' + type)}>
            <Link
              onClick={this.pushAnalytics}
              to={{ pathname: videoPath, search: queryParams, state: { railAnalytics: railAnalyticsVideo } }}
            >
              {video.title}
            </Link>
          </div>

          <VideoItemProgramName program={video.program} type={type} railAnalytics={railAnalyticsProgram} />

          {this.viewCount}

          <div className={classnames('d-none mt-6 fg-white ls-0-2 wb-breakword ww-breakword', 'video-item__description--' + type)}>
            {video.description}
          </div>

          {this.trashButton}
        </div>
      </div>
    );
  }

  private get trashButton(): JSX.Element | undefined {
    return this.props.onTrashButtonClick && (
      <ButtonIcon
        className="d-none position-absolute top-0 right-0 video-item__trash-button"
        modifier="clear"
        icon={IconTrash}
        onClick={this.props.onTrashButtonClick}
      />
    );
  }

  private get viewCount(): JSX.Element {
    const { isShowViewCount = true, type, video } = this.props;
    return isShowViewCount
      ? (<IconContent
        className={classnames('mt-2 mt@md-4 fs-12 fg-gray-medium', 'video-item__view-count--' + type)}
        classNameIcon={classnames('fs-14', 'video-item__view-count-icon--' + type)}
        icon={IconViews}
      >
        {formatViewCountNumber(video.viewCount)}
      </IconContent>)
      : <div className="mt-20" />;
  }

  private pushAnalytics(): void {
    const { eventTrack, video } = this.props;
    if (eventTrack) {
      pushEventTracking(
        eventTrack['eventDetails.category'], eventTrack['eventDetails.action'],
        (video.program ? video.program.name + ' - ' : '') + video.title,
        eventTrack.custom ? eventTrack.custom : undefined
      );
    }
  }
}
